import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import { AccountRow } from '../components/AccountRow'
import { Mark } from '../components/Mark'
import { isNativeApp } from '../native'
import { CloseAccountLink } from '../components/CloseAccount'

/*
  Billing.

  Thirty days free, card up front, then it bills. The card is the condition of the trial
  rather than an afterthought, so this screen says that in the sentence next to the button
  instead of in terms nobody reads.

  Every number on this screen comes from the API, which reads Stripe:TrialDays. Nothing
  here is written down: this is the screen the assistant and the marketing page both point
  at for the real figure, so it has to be the one place that cannot be stale.

  Card details never touch this application. The button opens Stripe Checkout and managing
  it afterwards opens Stripe's portal: cards, invoices, receipts and cancelling are all
  somebody else's solved problem, and a worse copy of them here would also be a copy that
  handles card numbers.
*/

export interface Billing {
  configured: boolean
  status: string | null
  entitled: boolean
  exempt: boolean
  trialDaysLeft: number | null
  trialEndsAt: string | null
  renewsAt: string | null
  /** Cancelled, but still running until the period ends. */
  cancelling: boolean
  /** The day access stops. Null unless it has been cancelled. */
  endsAt: string | null
  hasSubscription: boolean
  priceUsd: number
  trialDays: number
}

export function useBilling() {
  const [billing, setBilling] = useState<Billing | null>(null)

  const load = useCallback(async () => {
    /*
      Stripe's portal sends the owner back with ?billing=updated. The webhook is what keeps
      this record current and it is fast, but not faster than a browser redirect: somebody
      who cancels and lands here a second later would be shown the subscription they had
      just cancelled. One read of Stripe on the return trip settles it.
    */
    if (new URLSearchParams(window.location.search).get('billing') === 'updated') {
      try { await api.post('/api/billing/refresh', {}) } catch { /* the webhook still will */ }
    }
    try { setBilling(await api.get<Billing>('/api/billing')) } catch { setBilling(null) }
  }, [])

  useEffect(() => { void load() }, [load])
  return { billing, reload: load }
}

/** Sends the browser to Stripe. Returns an error string, or null when it is navigating. */
async function go(path: string): Promise<string | null> {
  try {
    const { url } = await api.post<{ url: string }>(path, {})
    window.location.href = url
    return null
  } catch (e) {
    return e instanceof Error ? e.message : 'Could not reach Stripe'
  }
}

/*
  Billing on Settings: one row saying what this account is on, and one button.

  It used to be a card of prose per state. Every one of those sentences is still here, moved
  into the note, because the states are genuinely different and an owner who has just
  cancelled needs a different sentence from one who has never started. What went is the
  paragraph shape, so Billing takes one line on a screen it shares with everything else.
*/
/*
  Two of these, and which one you get is not a style choice.

  Inside the iOS app, guideline 3.1.3(f) only holds while there is no purchase in the app and
  no call to action to purchase anywhere else. A "Start the 30-day trial" button that opens
  Stripe is both. So the app gets a card that reports the state and stops: no price, no
  button, no Stripe, no mention of the website. See src/native.ts for what that is worth.
*/
export function BillingCard() {
  return isNativeApp() ? <NativeAccountCard /> : <WebBillingCard />
}

/** What the app is allowed to say: which state this account is in, and nothing else. */
function NativeAccountCard() {
  const { billing } = useBilling()
  if (!billing) return null

  return (
    <div className="card card--rows">
      <div className="card__title">Account</div>
      <AccountRow label={nativeState(billing).label} note={nativeState(billing).note} />
    </div>
  )
}

/**
 * The same states `plan()` knows, with every price, card and call to action taken out.
 *
 * Written separately rather than filtered out of `plan()` on the way past, because a filter
 * is a thing somebody adds a state to and forgets, and the cost of forgetting here is the
 * app being pulled.
 */
function nativeState(b: Billing): { label: string; note: string } {
  if (b.exempt) return { label: 'Not billed', note: 'This account is not billed.' }

  if (b.cancelling) {
    return {
      label: 'Closing',
      note: b.endsAt
        ? `Your board stays open until ${day(b.endsAt)}.`
        : 'Your board stays open until the end of the current period.',
    }
  }

  if (b.status === 'trialing') {
    const left = b.trialDaysLeft
    return {
      label: 'Trial',
      note: left === 0 ? 'Ends today.' : `${left} day${left === 1 ? '' : 's'} left.`,
    }
  }

  if (b.status === 'active') {
    return {
      label: 'Active',
      note: b.renewsAt ? `Renews on ${day(b.renewsAt)}.` : 'Renews monthly.',
    }
  }

  if (b.status === 'past_due' || b.status === 'unpaid') {
    return { label: 'Payment failed', note: 'The last payment did not go through.' }
  }

  return { label: 'Not active', note: 'This account has no access at the moment.' }
}

function WebBillingCard() {
  const { billing } = useBilling()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!billing) return null

  const go2 = async (path: string) => {
    setBusy(true)
    setError(await go(path))
    setBusy(false)
  }

  const manage = () => go2('/api/billing/portal')
  const start = () => go2('/api/billing/checkout')

  const state = plan(billing)

  return (
    <div className="card card--rows">
      <div className="card__title">Billing</div>

      {error && <div className="signin__error">{error}</div>}

      <AccountRow
        label={state.label}
        note={state.note}
        action={state.action && (
          <button
            className={`btn btn--small${state.primary ? ' btn--primary' : ''}`}
            disabled={busy || (state.action === 'start' && !billing.configured)}
            onClick={() => void (state.action === 'start' ? start() : manage())}
          >
            {busy ? 'Opening Stripe' : state.cta}
          </button>
        )}
      />

      {!billing.configured && !billing.exempt && (
        <AccountRow label="Not configured" note="Billing is not set up on this server." />
      )}
    </div>
  )
}

/** What this account is on, in the two words and one sentence the row needs. */
function plan(b: Billing): {
  label: string
  note: string
  action?: 'start' | 'manage'
  primary?: boolean
  cta?: string
} {
  /*
    Comped. Never charged by us, whatever Stripe thinks.

    The subscription still matters though. Somebody comped after they had already signed up
    has a live Stripe subscription that will charge their card on its own schedule, and
    hiding the button because the product no longer bills them takes away the only way they
    had to stop it. That is the worst version of this screen: "Nothing to do here" above a
    card that is about to be charged $249.

    So the button stays whenever there is something on file, and the note says plainly that
    cancelling costs them nothing.
  */
  if (b.exempt) {
    return b.hasSubscription
      ? {
          label: 'Not billed',
          note: 'This account is not billed. There is still a subscription on file from '
              + 'before, and cancelling it changes nothing about your access.',
          action: 'manage', cta: 'Manage the subscription',
        }
      : { label: 'Not billed', note: 'This account is not billed. Nothing to do here.' }
  }

  // Cancelled and still running. Checked before the status, because Stripe leaves that at
  // trialing or active until the period ends: reading the status first quotes somebody a
  // renewal date for a subscription they have already cancelled.
  if (b.cancelling) {
    return {
      label: 'Cancelled',
      note: (b.status === 'trialing'
        ? 'Your trial runs to the end and you will not be charged.'
        : 'It will not renew.')
        + (b.endsAt
          ? ` The board stays open until ${day(b.endsAt)}, then this account closes.`
          : ' The board stays open until the end of the current period.'),
      action: 'manage', primary: true, cta: 'Reactivate',
    }
  }

  if (b.status === 'trialing') {
    const left = b.trialDaysLeft
    return {
      label: 'Trial',
      note: (left === 0
        ? 'Ends today.'
        : `${left} day${left === 1 ? '' : 's'} left.`)
        + ` It becomes $${b.priceUsd} a month${b.trialEndsAt ? ` on ${day(b.trialEndsAt)}` : ''}, on the card you gave.`,
      action: 'manage', cta: 'Manage',
    }
  }

  if (b.status === 'active') {
    return {
      label: `$${b.priceUsd} a month`,
      note: b.renewsAt ? `Next payment on ${day(b.renewsAt)}.` : 'Renews monthly.',
      action: 'manage', cta: 'Manage',
    }
  }

  if (b.status === 'past_due' || b.status === 'unpaid') {
    return {
      label: 'Payment failed',
      note: 'The last payment did not go through. Update the card and it carries on where it left off.',
      action: 'manage', primary: true, cta: 'Update the card',
    }
  }

  return {
    label: 'No subscription',
    note: `${b.trialDays} days free, then $${b.priceUsd} a month. The card is taken now and the `
        + `first charge is on day ${b.trialDays + 1}; cancel before then and you are not billed at all.`,
    action: 'start', primary: true, cta: `Start the ${b.trialDays}-day trial`,
  }
}

/*
  The wall. Shown instead of the product when the subscription has lapsed or was never
  started, and it is the only thing an unentitled account can see.

  It is not a nag screen: the API returns 402 for everything behind it, so this is an
  honest picture of what the account can currently do rather than a suggestion.
*/
export function Paywall({ reason }: { reason: string | null }) {
  return isNativeApp() ? <NativeWall /> : <WebPaywall reason={reason} />
}

/*
  The wall, inside the app, and the single most rewritable screen in this repo.

  Guideline 3.1.3(f) is what lets a free companion app to a paid web tool skip in-app
  purchase, and it holds only while there is no purchasing in the app and no call to action
  to purchase outside it. Everything obvious you might add here breaks it: the price, the
  word subscribe, a link to boardium.co, a "manage your account online" hint, an email
  template that says where to pay. At $249 a month the difference is $37.35 per subscriber
  per month at best.

  So this names the state, says the work is safe, and offers the one action that must never
  be behind a wall. It does not help the owner pay, because it cannot.
*/
function NativeWall() {
  const [busy, setBusy] = useState(false)

  const out = async () => {
    setBusy(true)
    try { await api.post('/auth/logout', {}) } catch { /* leaving anyway */ }
    window.location.href = '/'
  }

  return (
    <div className="signin">
      <div className="signin__card">
        <div className="l-lockup" style={{ marginBottom: 'var(--space-3)' }}>
          <Mark className="l-lockup__mark" />
          <img className="l-lockup__word" src="/brand/wordmark-ink.png" alt="Boardium" />
        </div>

        <h2 className="l-h2 l-auth__title">This account is not active</h2>

        <p className="muted" style={{ marginBottom: 'var(--space-4)' }}>
          Nothing has been deleted. Every meeting, document and figure is where you left it,
          and it all opens again the moment this account is back in service.
        </p>

        <button className="btn btn--primary btn--block" disabled={busy} onClick={() => void out()}>
          Sign out
        </button>

        {/*
          Reachable from the wall on purpose. Somebody locked out is the likeliest person to
          want their account closed, and a delete you can only reach while you are paying is
          not the one 5.1.1(v) asks for. It says nothing about price or subscriptions, so
          3.1.3(f) has no opinion about it either.
        */}
        <div className="signin__switch">
          <CloseAccountLink />
        </div>
      </div>
    </div>
  )
}

function WebPaywall({ reason }: { reason: string | null }) {
  const { billing } = useBilling()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const started = billing?.hasSubscription ?? false

  const act = async () => {
    setBusy(true)
    setError(await go(started ? '/api/billing/portal' : '/api/billing/checkout'))
    setBusy(false)
  }

  return (
    <div className="signin">
      <div className="signin__card">
        {/*
          The mark and the wordmark, not the product name set in the UI font.

          This is the first screen a new owner sees after signing in, and the one that asks
          for a card. The ink wordmark rather than the white one because this card is white;
          `l-auth__brand` is deliberately not used, since it is display:none below the width
          where the landing page's navy panel disappears.
        */}
        <div className="l-lockup" style={{ marginBottom: 'var(--space-3)' }}>
          <Mark className="l-lockup__mark" />
          <img className="l-lockup__word" src="/brand/wordmark-ink.png" alt="Boardium" />
        </div>

        <h2 className="l-h2 l-auth__title">
          {started ? 'Your subscription has lapsed' : 'Start your trial'}
        </h2>

        <p className="muted" style={{ marginBottom: 'var(--space-4)' }}>
          {reason ?? 'This account needs an active subscription.'}
        </p>

        {!started && billing && (
          <p className="muted" style={{ marginBottom: 'var(--space-4)' }}>
            {billing.trialDays} days free, then ${billing.priceUsd} a month. We take the card
            now so it can carry straight on; cancel inside {billing.trialDays} days and you
            are not charged.
          </p>
        )}

        {error && <div className="signin__error">{error}</div>}

        <button className="btn btn--primary btn--block" disabled={busy} onClick={act}>
          {busy ? 'Opening Stripe'
            : started ? 'Update billing'
            : `Start the ${billing?.trialDays ?? 30}-day trial`}
        </button>

        <div className="signin__switch">
          {/* Somebody locked out still has to be able to leave, and to come back as
              somebody else. Sign-out is the one action that must never be behind a wall. */}
          <button
            className="linkish"
            onClick={async () => {
              await api.post('/auth/logout', {})
              window.location.href = '/'
            }}
          >
            Sign out
          </button>
          <CloseAccountLink />
        </div>
      </div>
    </div>
  )
}

/** A trial banner, for the shell. Null unless there is something worth saying. */
export function TrialBanner({ billing }: { billing: Billing | null }) {
  /*
    A comped account is never billed and never closes, so it has nothing to count down to.

    This has to come first. Both branches below are about money: one counts the days to a
    trial ending, the other the days to a cancelled account closing, and neither happens to
    somebody who is billing_exempt. Without this, comping an owner and then having them
    cancel their subscription (which is the tidy way to do it, so no card is ever charged)
    puts "This account closes in 14 days" across the top of a board that closes on no date
    at all. The screen below already checks exempt before cancelling in both of its own
    branches; this banner is the one place that did not.
  */
  if (billing?.exempt) return null

  // A cancelled account gets its own countdown, whatever the status underneath says. The
  // last days before a board closes are worth more warning than the last days of a trial.
  if (billing?.cancelling && billing.endsAt) {
    const days = Math.ceil((new Date(billing.endsAt).getTime() - Date.now()) / 86_400_000)
    if (days > 14) return null
    return (
      <aside className="nudge nudge--trial" role="status">
        <div className="nudge__body">
          <p className="nudge__label">
            {days <= 0 ? 'This account closes today' : `This account closes in ${days} day${days === 1 ? '' : 's'}`}
          </p>
          <p className="nudge__why">
            You cancelled, so it will not renew on {day(billing.endsAt)}. Reactivate from
            Settings any time before then and nothing is interrupted.
          </p>
        </div>
      </aside>
    )
  }

  if (!billing || billing.status !== 'trialing') return null
  const left = billing.trialDaysLeft ?? 0

  // Silent for the first week. A countdown from day one is a nag; the last few days are
  // information somebody actually wants before the card is charged.
  if (left > 5) return null

  /*
    The app gets the countdown and not the sentence under it. Knowing the trial ends on
    Thursday is a fact about the account; "it becomes $249 a month on the card you gave" is
    the price and the payment method, which is the shape of thing 3.1.3(f) does not allow an
    app to carry. Same fact, one of them sellable.
  */
  if (isNativeApp()) {
    return (
      <aside className="nudge nudge--trial" role="status">
        <div className="nudge__body">
          <p className="nudge__label">
            {left === 0 ? 'Your trial ends today' : `${left} day${left === 1 ? '' : 's'} left of your trial`}
          </p>
        </div>
      </aside>
    )
  }

  return (
    <aside className="nudge nudge--trial" role="status">
      <div className="nudge__body">
        <p className="nudge__label">
          {left === 0 ? 'Your trial ends today' : `${left} day${left === 1 ? '' : 's'} left of your trial`}
        </p>
        <p className="nudge__why">
          It becomes ${billing.priceUsd} a month after that, on the card you gave. Cancel any
          time before then from Settings.
        </p>
      </div>
    </aside>
  )
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long' })
}
